import { Component } from '@theme/component';

class BVFuturisticMotion extends Component {
  #abort;
  #observer;
  #heroObserver;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #pointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  #seen = new WeakSet();
  #heroes = new Set();
  #targets = new Set();
  #scrambles = new Map();
  #frame = 0;
  #pending;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.#observer.unobserve(entry.target);
        this.#scramble(entry.target);
      }
    }, { threshold: 0.3 });
    this.#heroObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) entry.target.toggleAttribute('data-in-view', entry.isIntersecting);
    }, { threshold: 0.05 });
    document.addEventListener('pointermove', this.#move, { passive: true, signal });
    document.addEventListener('pointerout', this.#out, { passive: true, signal });
    document.addEventListener('shopify:section:load', this.#initialize, { signal });
    document.addEventListener('visibilitychange', this.#reset, { signal });
    this.#motion.addEventListener('change', this.#reset, { signal });
    this.#pointer.addEventListener('change', this.#reset, { signal });
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reset();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#heroObserver?.disconnect();
    for (const hero of this.#heroes) hero.removeAttribute('data-in-view');
    this.#heroes.clear();
    this.#targets.clear();
  }

  #initialize = () => {
    for (const hero of document.querySelectorAll('.bv-future-hero')) {
      if (this.#heroes.has(hero)) continue;
      this.#heroes.add(hero);
      this.#heroObserver.observe(hero);
    }
    if (this.dataset.scramble !== 'true' || this.#motion.matches || window.Shopify?.designMode) return;
    for (const text of document.querySelectorAll('[data-bv-scramble], .bv-story__eyebrow, .section-resource-list h2, .section-resource-list h3')) {
      if (this.#seen.has(text) || text.children.length || text.textContent.trim().length > 64) continue;
      this.#seen.add(text);
      this.#observer.observe(text);
    }
  };

  #move = (event) => {
    if (this.#motion.matches || !this.#pointer.matches || event.pointerType === 'touch' || window.Shopify?.designMode) return;
    if (!(event.target instanceof Element)) return;
    let target = null;
    let type = 'tilt';
    if (!target && this.dataset.magnet === 'true') {
      target = event.target.closest('#MainContent .button:not(:disabled), #MainContent .button-secondary, #MainContent .bv-future-hero__scroll');
      type = 'magnet';
    }
    if (!target) return;
    this.#pending = { target, type, x: event.clientX, y: event.clientY };
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const pending = this.#pending;
      if (!pending?.target.isConnected) return;
      const { target, type, x, y } = pending;
      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const px = Math.max(-1, Math.min(1, (x - rect.left) / rect.width * 2 - 1));
      const py = Math.max(-1, Math.min(1, (y - rect.top) / rect.height * 2 - 1));
      this.#targets.add(target);
      target.setAttribute('data-pointer-active', '');
      if (type === 'tilt') {
        const strength = Math.max(1, Math.min(8, Number(this.dataset.strength) || 4));
        target.setAttribute('data-bv-future-tilt', '');
        target.style.setProperty('--bv-tilt-x', `${-py * strength}deg`);
        target.style.setProperty('--bv-tilt-y', `${px * strength}deg`);
      } else {
        target.setAttribute('data-bv-future-magnet', '');
        target.style.setProperty('--bv-magnet-x', `${px * 6}px`);
        target.style.setProperty('--bv-magnet-y', `${py * 6}px`);
      }
    });
  };

  #out = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('[data-pointer-active]');
    if (!target || event.relatedTarget instanceof Node && target.contains(event.relatedTarget)) return;
    if (this.#pending?.target === target) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      this.#pending = null;
    }
    this.#clearTarget(target);
  };

  #clearTarget(target) {
    target.removeAttribute('data-pointer-active');
    for (const name of ['--bv-tilt-x', '--bv-tilt-y', '--bv-magnet-x', '--bv-magnet-y']) target.style.removeProperty(name);
  }

  #reset = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#pending = null;
    for (const target of this.#targets) this.#clearTarget(target);
    for (const [element, state] of this.#scrambles) {
      cancelAnimationFrame(state.frame);
      element.replaceChildren(state.original);
    }
    this.#scrambles.clear();
  };

  #scramble(element) {
    if (this.#motion.matches || document.hidden || !element.isConnected) return;
    const text = element.textContent;
    const original = document.createDocumentFragment();
    for (const child of [...element.childNodes]) original.append(child);
    const accessible = document.createElement('span');
    accessible.className = 'visually-hidden';
    accessible.textContent = text;
    const visual = document.createElement('span');
    visual.setAttribute('aria-hidden', 'true');
    visual.textContent = text;
    element.append(accessible, visual);
    const state = { original, frame: 0 };
    this.#scrambles.set(element, state);
    const started = performance.now();
    let last = 0;
    const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const tick = (time) => {
      const progress = Math.min(1, (time - started) / 650);
      if (progress >= 1 || !element.isConnected || this.#motion.matches) {
        element.replaceChildren(original);
        this.#scrambles.delete(element);
        return;
      }
      if (time - last > 55) {
        const reveal = Math.floor(progress * text.length);
        visual.textContent = [...text].map((character, index) => index < reveal || /\s|[^a-zA-Z0-9]/.test(character) ? character : characters[Math.floor(Math.random() * characters.length)]).join('');
        last = time;
      }
      state.frame = requestAnimationFrame(tick);
    };
    state.frame = requestAnimationFrame(tick);
  }
}

if (!customElements.get('bv-futuristic-motion')) customElements.define('bv-futuristic-motion', BVFuturisticMotion);

